import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/auth';
import { useToast } from '../hooks/use-toast';
import HeroFloatingLogos from '../components/HeroFloatingLogos';
import BrandLogo from '../components/BrandLogo';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const { toast } = useToast();
  const redirect = new URLSearchParams(loc.search).get('redirect') || '/';

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast({ title: 'Please fill all fields', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      const u = await login(email, password);
      toast({ title: 'Welcome back!', description: u.name });
      if (u.role === 'admin' || u.role === 'super_admin') {
        navigate(redirect.startsWith('/admin') ? redirect : '/admin');
      } else {
        navigate(redirect);
      }
    } catch (err) {
      toast({ title: 'Login failed', description: err.response?.data?.detail || 'Invalid credentials', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16 min-h-screen grid lg:grid-cols-2">
      <div className="relative overflow-hidden mesh-bg hidden lg:block">
        <div className="absolute inset-0 dot-pattern opacity-40 pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-gradient-to-br from-[#6b47dc]/25 to-transparent rounded-full blur-3xl blob" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-gradient-to-br from-[#ff5722]/25 to-transparent rounded-full blur-3xl blob" />
        <div className="absolute inset-x-8 top-12 bottom-32">
          <HeroFloatingLogos />
        </div>
        <div className="relative z-10 flex flex-col justify-end p-12 text-white h-full pointer-events-none">
          <h2 className="text-4xl font-extrabold leading-tight mb-3 text-gray-900">Welcome back to your learning journey</h2>
          <p className="text-lg text-gray-600">Continue where you left off and unlock more skills.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <BrandLogo size="lg" showWordmark={true} />
          </Link>
          <h1 className="text-3xl font-extrabold mb-2">Sign in</h1>
          <p className="text-gray-500 mb-8">Access your dashboard and continue learning.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none" placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={show ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none" placeholder="Enter password" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" className="accent-[#6b47dc]" /> Remember me</label>
              <a href="#" className="text-[#6b47dc] hover:underline font-semibold">Forgot password?</a>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#6b47dc] hover:bg-[#5a3ac0] text-white font-bold h-12 btn-shine">
              {loading ? 'Signing in...' : <>Sign in <ArrowRight size={16} className="ml-1" /></>}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-8">
            Don't have an account? <Link to="/signup" className="text-[#6b47dc] font-bold hover:underline">Sign up free</Link>
          </p>

        </div>
      </div>
    </div>
  );
}
